import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IMappingSize from '../../interfaces/IMappingSize'
import { SearchRequest, MappingSizeSaveRequest } from '../../services/api/interfaces/ApiRequest'
import { mappingSizeList, mappingSizeSave } from '../../services/api'

interface MappingSizeContextState extends BaseContextState {
  mappingSizes: Array<IMappingSize> | null,
  mappingSize: IMappingSize | null
}

interface MappingSizeContextData extends MappingSizeContextState {
  resetErrors: Function,
  requestMappingSizeList: (args: SearchRequest) => Promise<any>,
  requestMappingSizeFind: (args: SearchRequest) => Promise<any>,
  requestMappingSizeSave: (args: MappingSizeSaveRequest) => void,
}

// Create context
const { Provider, Consumer } = createContext({} as MappingSizeContextData)

// Export consumer
export const MappingSizeConsumer = Consumer

export class MappingSizeProvider extends BaseContextProvider {
  state: MappingSizeContextState = {
    mappingSizes: null,
    mappingSize: null
  }

  /**
   * Make api request for mapping size list
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingSizeList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await mappingSizeList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'mappingSizes'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping size finder
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingSizeFind = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        mappingSize: null
      }, async () => {
        payload.options = {
          ...payload.options,
          _single: true
        }
        payload.search = {
          ...payload.search,
          shelter: {
            with: {
              _fields: ["id", "uuid", "name"],
            }
          }
        }
  
        const response = await mappingSizeList(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'mappingSize'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping size save
   * 
   * @param {MappingSizeSaveRequest} payload 
   */
  requestMappingSizeSave = (payload: MappingSizeSaveRequest) => {
    // No need to null `mappingSize` on save: the form stays mounted (its render
    // no longer depends on this value) and re-fetches fresh whenever the modal reopens.
    return new Promise<void>((resolve, reject) => this.setState({
      fetching: true,
      error: null,
      // mappingSize: null
    }, async () => {
      try {
        const response = await mappingSizeSave(payload)

        // Process our response
        const result = await this.processResponse(response, {
          mappings: [
            { origin: 'mapping_size', destination: 'mappingSize' }
          ]
        })

        // throw if error happend
        if (result.error) {
          return reject(new Error(result.error))
        }
      } finally {
        resolve()
      }
    }))
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestMappingSizeList: this.requestMappingSizeList,
          requestMappingSizeFind: this.requestMappingSizeFind,
          requestMappingSizeSave: this.requestMappingSizeSave
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
